import { 
  ShieldCheck, 
  FileCheck, 
  Zap, 
  ClipboardCheck, 
  Building2, 
  Wrench,
  FileText,
  AlertTriangle,
  CheckCircle,
  Phone
} from 'lucide-react';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
}

function ServiceCard({ icon, title, description, features }: ServiceCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-center mb-4">
        <div className="p-3 bg-blue-100 rounded-lg">
          {icon}
        </div>
        <h3 className="ml-4 text-xl font-bold text-slate-900">{title}</h3>
      </div>
      <p className="text-slate-600 mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ServicesPage() {
  const services = [
    {
      icon: <FileCheck className="w-6 h-6 text-blue-500" />,
      title: "Fire Alarm System Design",
      description: "Complete NFPA 72 compliant fire alarm system design for new construction and retrofit projects.",
      features: [
        "Full system layout and device placement",
        "NFPA 72, NEC 760, IBC/IFC compliance",
        "Notification appliance calculations",
        "Battery backup sizing and calculations",
        "Riser diagrams and panel schedules",
        "Specifications and equipment schedules"
      ]
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-blue-500" />,
      title: "AI-Enhanced Plan Review",
      description: "Advanced AI-powered review of fire alarm plans to catch errors and ensure code compliance before submission.",
      features: [
        "Automated NFPA 72 compliance checking",
        "Device spacing and coverage analysis",
        "Code violation detection",
        "Notification appliance verification",
        "Comprehensive review reports",
        "Faster approval process"
      ]
    },
    {
      icon: <Building2 className="w-6 h-6 text-blue-500" />,
      title: "Commercial Fire Alarm Systems",
      description: "Specialized design services for commercial buildings of all types and occupancies.",
      features: [
        "Office buildings and high-rises",
        "Retail and shopping centers",
        "Healthcare facilities",
        "Educational institutions",
        "Industrial and warehouse facilities",
        "Mixed-use developments"
      ]
    },
    {
      icon: <Zap className="w-6 h-6 text-blue-500" />,
      title: "System Upgrades & Retrofits",
      description: "Modernize existing fire alarm systems with current technology and code requirements.",
      features: [
        "Legacy system replacement",
        "Code compliance upgrades",
        "Addressable system conversions",
        "Wireless device integration",
        "Voice evacuation systems",
        "Mass notification systems"
      ]
    },
    {
      icon: <ClipboardCheck className="w-6 h-6 text-blue-500" />,
      title: "Permit & Approval Services",
      description: "Complete permit package preparation and coordination with authorities having jurisdiction.",
      features: [
        "AHJ submittal packages",
        "Plan check coordination",
        "Code compliance documentation",
        "Response to plan check corrections",
        "Fire marshal coordination",
        "Certificate of occupancy support"
      ]
    },
    {
      icon: <Wrench className="w-6 h-6 text-blue-500" />,
      title: "Testing & Inspection Support",
      description: "Technical support for system acceptance testing and annual inspections.",
      features: [
        "Acceptance test procedures",
        "NFPA 72 Chapter 14 compliance",
        "Test documentation review",
        "Deficiency analysis",
        "Inspection support",
        "Record drawings and documentation"
      ]
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-500" />,
      title: "As-Built Documentation",
      description: "Accurate as-built drawings and system documentation for facility management.",
      features: [
        "Record drawing preparation",
        "Device location verification",
        "System programming documentation",
        "O&M manual compilation",
        "Digital file delivery",
        "CAD and PDF formats"
      ]
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-blue-500" />,
      title: "Emergency Consulting",
      description: "Rapid response technical consulting for urgent fire alarm system issues.",
      features: [
        "Code compliance questions",
        "System design review",
        "Problem resolution",
        "AHJ coordination",
        "Expert witness services",
        "Same-day response available"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 data-testid="page-h1-services" className="text-4xl md:text-5xl font-bold mb-4">
              Services
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Comprehensive fire alarm design, review, and consulting services for California projects
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <ShieldCheck className="w-5 h-5 mr-2" />
                <span>NFPA 72 Certified</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <Building2 className="w-5 h-5 mr-2" />
                <span>Licensed Engineer</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <Zap className="w-5 h-5 mr-2" />
                <span>AI-Enhanced</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              features={service.features}
            />
          ))}
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
              Why Choose Cal-Spec Fire Alarm Design?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Code Expertise
                  </h3>
                  <p className="text-slate-600">
                    Deep knowledge of NFPA 72, NEC 760, IBC, and California-specific requirements
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Fast Turnaround
                  </h3>
                  <p className="text-slate-600">
                    AI-enhanced workflows enable faster design and review without compromising quality
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Licensed Professional
                  </h3>
                  <p className="text-slate-600">
                    All designs stamped by California licensed electrical engineer
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Competitive Pricing
                  </h3>
                  <p className="text-slate-600">
                    Efficient processes allow us to offer competitive rates without sacrificing quality
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Get a free consultation and quote for your fire alarm system design needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:8185338313"
              className="inline-flex items-center px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call (818) 533-8313
            </a>
            <a 
              href="#contact"
              className="inline-flex items-center px-8 py-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold rounded-lg transition-colors duration-200"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
